import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RateReviewIcon from "@mui/icons-material/RateReview";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { AccountPageLayout } from "components/common/AccountPageLayout";
import { CopyLinkButton } from "components/labelling/CopyLinkButton";
import {
  ManualReviewEventProvider,
  useManualReviewEvents,
} from "components/manualReview/contexts/ManualReviewEventContext";
import { DayTimeline } from "components/manualReview/DayTimeline/DayTimeline";
import { ManualReviewVideoControls } from "components/manualReview/ManualReviewVideoControls";
import { ActiveChargeScheduleMaterials } from "components/identifyEventsTool/tool/ActiveChargeScheduleMaterials";
import { RailcarMovements } from "components/identifyEventsTool/tool/RailcarMovements";
import { MassDataPanel } from "components/identifyEventsTool/tool/MassDataPanel";
import { useIsBehindFeatureFlag } from "hooks/useFeatureFlag";
import { filterReviewPeriods } from "components/manualReview/utils/formatters";
import { SafeDateTimePicker } from "components/SafeDateTimePicker";
import { ToggleableAnglesVideoPlayer } from "components/scrapEyeDataDashboard/VideoPlayer/components/ToggleableAnglesVideoPlayer";
import { HotkeyProvider } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/HotkeyContext";
import { TimelineZoomProvider } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/TimelineZoomContext";
import {
  VideoControlProvider,
  useVideoControlActions,
  useVideoControlState,
} from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoControlContext";
import { VideoPlayerGroupProvider } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoPlayerGroupContext";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { useTenantTranslation } from "hooks/formatters";
import { usePermission } from "hooks/permissions";
import { useTenant } from "hooks/settings";
import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import {
  LabellerTimeline,
  ReviewPeriodData,
  useGetEventsForReviewQuery,
  useGetReviewDayOverviewQuery,
} from "src/store/api/generatedApi";
import { PLAYBACK_SCOPE } from "components/scrapEyeDataDashboard/VideoPlayer/constants";

dayjs.extend(utc);

const VIEWING_WINDOW_MINUTES = 30;

export const ViewerPage = () => {
  const { location } = useParams<{ location: string }>();

  if (!location) return null;

  return (
    <HotkeyProvider>
      <VideoControlProvider>
        <VideoPlayerGroupProvider location={location}>
          <ViewerPageInner location={location} />
        </VideoPlayerGroupProvider>
      </VideoControlProvider>
    </HotkeyProvider>
  );
};

const ViewerPageInner = ({ location }: { location: string }) => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isReviewer = usePermission(["video_label_reviewing"]);
  const isRailcarMovementsEnabled =
    useIsBehindFeatureFlag("rail_car_movements");

  const rawTimestamp = searchParams.get("t");
  const timestamp = useMemo(() => {
    if (rawTimestamp) {
      const parsed = dayjs.utc(rawTimestamp);
      if (parsed.isValid()) return parsed;
    }
    return dayjs.utc().subtract(1, "hour");
  }, [rawTimestamp]);

  const windowStart = useMemo(
    () => timestamp.subtract(VIEWING_WINDOW_MINUTES / 2, "minute"),
    [timestamp]
  );
  const windowEnd = useMemo(
    () => timestamp.add(VIEWING_WINDOW_MINUTES / 2, "minute"),
    [timestamp]
  );

  const duration = useMemo(
    () => windowEnd.diff(windowStart, "second"),
    [windowStart, windowEnd]
  );

  const dayStart = useMemo(() => timestamp.startOf("day"), [timestamp]);
  const dayEnd = useMemo(() => dayStart.add(1, "day"), [dayStart]);

  const handleWindowChange = useCallback(
    (newStart: Dayjs) => {
      const newCenter = newStart.add(VIEWING_WINDOW_MINUTES / 2, "minute");
      setSearchParams({ t: newCenter.toISOString() });
    },
    [setSearchParams]
  );

  const actions = useVideoControlActions();
  useEffect(() => {
    actions.reset();
    actions.updateDuration(duration);

    const initialOffset = timestamp.diff(windowStart, "second");
    actions.seekTo(initialOffset);
  }, [windowStart, windowEnd, timestamp, actions, duration]);

  const eventsQuery = useGetEventsForReviewQuery({
    tenantName,
    windowStart: windowStart.toISOString(),
    windowEnd: windowEnd.toISOString(),
    location,
  });

  const dayOverviewQuery = useGetReviewDayOverviewQuery({
    tenantName,
    date: windowStart.format("YYYY-MM-DD"),
    location,
  });

  const reviewPeriods = useMemo(
    () =>
      filterReviewPeriods(
        dayOverviewQuery.data?.review_periods,
        windowStart,
        windowEnd
      ),
    [dayOverviewQuery.data?.review_periods, windowStart, windowEnd]
  );

  const labellerTimelines = eventsQuery.data?.labeller_timelines ?? [];
  const reviewEvents = eventsQuery.data?.review_events ?? [];

  return (
    <AccountPageLayout sx={{ p: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton
            onClick={() => void navigate(`/${tenantName}/tool/viewer`)}
            size="small"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h1">
            {t("videoViewer")} - {location}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <CopyLinkButton location={location} videoStart={windowStart} />
          {isReviewer ? (
            <OpenInReviewToolButton
              location={location}
              tenantName={tenantName}
              windowStart={windowStart}
            />
          ) : null}
          <SafeDateTimePicker
            value={timestamp}
            onChange={(newValue) => {
              if (newValue) setSearchParams({ t: newValue.toISOString() });
            }}
            timezone="UTC"
            enableAccessibleFieldDOMStructure={false}
            slotProps={{ textField: { size: "small" } }}
          />
        </Stack>
      </Box>
      <DayTimeline
        viewingWindowStart={windowStart}
        viewingWindowEnd={windowEnd}
        heats={[]}
        ticks={[]}
        reviewPeriods={dayOverviewQuery.data?.review_periods ?? []}
        onWindowChange={handleWindowChange}
        dayStart={dayStart}
        dayEnd={dayEnd}
      />
      <ManualReviewEventProvider
        events={reviewEvents}
        location={location}
        videoStart={windowStart}
        windowStart={windowStart}
        windowEnd={windowEnd}
      >
        <TimelineZoomProvider fullDuration={duration} videoStart={windowStart}>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              gap: 2,
              overflow: "hidden",
            }}
          >
            <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
              <ToggleableAnglesVideoPlayer
                hotkeyScope={PLAYBACK_SCOPE}
                storageKey="viewer"
                location={location}
                startIso={windowStart.toISOString()}
                endIso={windowEnd.toISOString()}
                showVideoControls={false}
              />
              <ViewerVideoControls
                location={location}
                windowStart={windowStart}
                duration={duration}
                reviewPeriods={reviewPeriods}
                labellerTimelines={labellerTimelines}
              />
            </Stack>
            <ViewerSidebar
              location={location}
              windowStart={windowStart}
              windowEnd={windowEnd}
              isRailcarMovementsEnabled={isRailcarMovementsEnabled}
            />
          </Box>
        </TimelineZoomProvider>
      </ManualReviewEventProvider>
    </AccountPageLayout>
  );
};

const ViewerSidebar = ({
  location,
  windowStart,
  windowEnd,
  isRailcarMovementsEnabled,
}: {
  location: string;
  windowStart: dayjs.Dayjs;
  windowEnd: dayjs.Dayjs;
  isRailcarMovementsEnabled: boolean;
}) => {
  return (
    <Stack
      direction="column"
      spacing={2}
      sx={{ width: 300, flexShrink: 0, overflowY: "auto" }}
    >
      {isRailcarMovementsEnabled ? (
        <>
          <RailcarMovements startTime={windowStart} endTime={windowEnd} />
          <ActiveChargeScheduleMaterials
            location={location}
            videoStart={windowStart}
            videoEnd={windowEnd}
          />
        </>
      ) : null}
      <MassDataPanel
        startIso={windowStart.toISOString()}
        endIso={windowEnd.toISOString()}
        videoStart={windowStart}
        location={location}
        events={[]}
        candidateEvents={[]}
      />
    </Stack>
  );
};

const ViewerVideoControls = ({
  location,
  windowStart,
  duration,
  reviewPeriods,
  labellerTimelines,
}: {
  location: string;
  windowStart: dayjs.Dayjs;
  duration: number;
  reviewPeriods: ReviewPeriodData[];
  labellerTimelines: LabellerTimeline[];
}) => {
  const { allEvents } = useManualReviewEvents();

  return (
    <ManualReviewVideoControls
      events={allEvents}
      reviewPeriods={reviewPeriods}
      videoStart={windowStart}
      duration={duration}
      labellerTimelines={labellerTimelines}
      actionButtons={
        <CopyLinkButton location={location} videoStart={windowStart} />
      }
    />
  );
};

const OpenInReviewToolButton = ({
  location,
  tenantName,
  windowStart,
}: {
  location: string;
  tenantName: string;
  windowStart: Dayjs;
}) => {
  const { t } = useTenantTranslation();
  const navigate = useNavigate();
  const { currentTime } = useVideoControlState();
  return (
    <Tooltip title={t("openInReviewTool")}>
      <IconButton
        onClick={() => {
          const absoluteTimestamp = windowStart
            .add(currentTime, "second")
            .utc()
            .format("YYYY-MM-DDTHH:mm:ss[Z]");
          void navigate(
            `/${tenantName}/tool/manual-review/${location}?date=${absoluteTimestamp}`
          );
        }}
        size="small"
      >
        <RateReviewIcon />
      </IconButton>
    </Tooltip>
  );
};
